import { courseMutations, courseQueries } from './index';
import { useQueryData } from '../apollo/query';
import { useMutationData } from '../apollo/mutation';
import {
  ICreateCourseInput,
  IUpdateCourseInput,
  IDeleteCourseInput,
  ICourse,
  IGetCourseInput,
  IListCourseInput,
  IQueryGetCourseArgs,
  IQueryListCourseArgs,
  IMutationCreateCourseArgs,
  IMutationUpdateCourseArgs,
  IMutationDeleteCourseArgs,
  IListRecentCoursesInput,
  IQueryListRecentCoursesArgs,
  ICourseData,
} from 'types/types';

import { IError, ISuccess } from 'types/Models';
import { useTranslationText } from 'locale';

interface Callback {
  create: {
    loading: boolean;
    createCourse: (input: ICreateCourseInput) => Promise<{ id?: string; error?: IError; success?: ISuccess }>;
  };
  update: {
    loading: boolean;
    updateCourse: (input: IUpdateCourseInput) => Promise<{ error?: IError; success?: ISuccess }>;
  };
  delete: {
    loading: boolean;
    deleteCourse: (input: IDeleteCourseInput) => Promise<{ error?: IError; success?: ISuccess }>;
  };
  get: {
    loading: boolean;
    course?: ICourse;
    error?: IError;
    getCourse: (input: IGetCourseInput) => void;
  };
  list: {
    loading: boolean;
    courseList: ICourse[];
    listCourse: (input: IListCourseInput) => void;
  };
  listPublic: {
    loading: boolean;
    courseList: ICourse[];
    listCoursePublic: (input: IListCourseInput) => void;
  };
  recentCourses: {
    loading: boolean;
    data?: ICourseData;
    recentCourses: (input: IListRecentCoursesInput) => void;
  };
}

export const useCourseApi = (): Callback => {
  const { getMessage } = useTranslationText();

  //MARK: GET QURERY
  const [getCourse, { data: dataGet, error: errorGet, loading: getLoading }] = useQueryData<
    { getCourse: ICourse },
    IQueryGetCourseArgs
  >({
    table: 'course',
    action: 'get',
    tData: 'getCourse',
    fetchPolicy: 'network-only',
    query: courseQueries.GET,
  });

  //MARK: GET LIST
  const [listCourse, { data: dataList, loading: loadingList }] = useQueryData<
    { listCourse: ICourse[] },
    IQueryListCourseArgs
  >({
    table: 'course',
    action: 'list',
    tData: 'listCourse',
    fetchPolicy: 'network-only',
    query: courseQueries.LIST,
  });

  const [listCoursePublic, { data: dataListPublic, loading: loadingListPublic }] = useQueryData<
    { listCoursePublic: ICourse[] },
    IQueryListCourseArgs
  >({
    table: 'course',
    action: 'list',
    tData: 'listCoursePublic',
    fetchPolicy: 'cache-and-network',
    query: courseQueries.LIST_PUBLIC,
  });

  const [listRecentCourses, { data: recentCourses, loading: recentCoursesLoading }] = useQueryData<
    { listRecentCourses: ICourseData },
    IQueryListRecentCoursesArgs
  >({
    table: 'course',
    action: 'list',
    tData: 'listRecentCourses',
    fetchPolicy: 'cache-and-network',
    query: courseQueries.LIST_RECENT,
  });

  //MARK: CREATE MUTATION
  const [createCourse, { loading: createLoading }] = useMutationData<
    { createCourse: string },
    IMutationCreateCourseArgs
  >({ mutation: courseMutations.CREATE, table: 'course', action: 'add', tData: 'createCourse' });

  //MARK: UPDATE MUTATION
  const [updateCourse, { loading: updateLoading }] = useMutationData<
    { updateCourse: boolean },
    IMutationUpdateCourseArgs
  >({ mutation: courseMutations.UPDATE, table: 'course', action: 'update' });

  //MARK: DELETE MUTATION
  const [deleteCourse] = useMutationData<{ deleteCourse: boolean }, IMutationDeleteCourseArgs>({
    mutation: courseMutations.DELETE,
    table: 'course',
    action: 'delete',
  });

  /********************** CUSTOM FUNCTION ************************* */

  //MARK:CREATE FUNCTION
  async function mutationCreateCourse(
    course: ICreateCourseInput,
  ): Promise<{ id?: string; error?: IError; success?: ISuccess }> {
    try {
      const create = await createCourse({ variables: { input: course } });
      return { id: create.data?.createCourse, success: getMessage('0', 'success') };
    } catch (error) {
      return { error: getMessage(error.message, 'error') };
    }
  }

  //MARK:UPDATE FUNCTION
  async function mutationUpdateCourse(course: IUpdateCourseInput): Promise<{ error?: IError; success?: ISuccess }> {
    try {
      await updateCourse({ variables: { input: course } });
      return { success: getMessage('0', 'success') };
    } catch (error) {
      return { error: getMessage(error.message, 'error') };
    }
  }

  //MARK:CREATE FUNCTION
  async function mutationDeleteCourse(
    deleteInput: IDeleteCourseInput,
  ): Promise<{ error?: IError; success?: ISuccess }> {
    try {
      await deleteCourse({ variables: { input: deleteInput } });
      return { success: getMessage('0', 'success') };
    } catch (error) {
      return { error: getMessage(error.message, 'error') };
    }
  }

  async function queryGetCourse(getInput: IGetCourseInput) {
    getCourse({ variables: { input: getInput } });
  }

  //MARK:GET FUNCTION
  function queryListCourse(listIput: IListCourseInput) {
    listCourse({ variables: { input: listIput } });
  }

  function queryListCoursePublic(listIput: IListCourseInput) {
    listCoursePublic({ variables: { input: listIput } });
  }

  function queryListRecentCourses(listIput: IListRecentCoursesInput) {
    listRecentCourses({ variables: { input: listIput } });
  }

  return {
    create: {
      loading: createLoading,
      createCourse: mutationCreateCourse,
    },
    update: {
      loading: updateLoading,
      updateCourse: mutationUpdateCourse,
    },

    delete: {
      loading: createLoading,
      deleteCourse: mutationDeleteCourse,
    },
    get: {
      error: errorGet ? getMessage(errorGet.message, 'error') : undefined,
      loading: dataGet?.getCourse ? false : getLoading,
      course: dataGet?.getCourse,
      getCourse: queryGetCourse,
    },
    list: {
      loading: (dataList?.listCourse || []).length > 0 ? false : loadingList,
      courseList: dataList?.listCourse || [],
      listCourse: queryListCourse,
    },
    listPublic: {
      loading: (dataListPublic?.listCoursePublic || []).length > 0 ? false : loadingListPublic,
      courseList: dataListPublic?.listCoursePublic || [],
      listCoursePublic: queryListCoursePublic,
    },
    recentCourses: {
      loading: recentCourses?.listRecentCourses ? false : recentCoursesLoading,
      data: recentCourses?.listRecentCourses,
      recentCourses: queryListRecentCourses,
    },
  };
};
